import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { serviceOrderService } from '../../services/serviceOrderService';
import html2pdf from 'html2pdf.js';
import {
  diffCalendarDays,
  formatDateLocaleLima,
  formatPrintDate,
  resolveOrderPaymentDateIso,
  resolveOrderPaymentPrintDate,
} from '../../utils/dateUtils';

const PrintServiceOrder = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const printContainerRef = useRef(null);

  useEffect(() => {
    loadOrder();
  }, [id]);

  // Removido el auto-print para mostrar primero la vista previa

  const loadOrder = async () => {
    try {
      const response = await serviceOrderService.getServiceOrder(id);
      if (response.success) {
        setOrder(response.data);
      }
    } catch (error) {
      console.error('Error al cargar orden:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando orden de servicio...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 text-lg">Orden no encontrada</p>
        </div>
      </div>
    );
  }

  const orderDate = formatPrintDate(order.order_date);

  const deliveryDate = order.expected_delivery_date
    ? formatPrintDate(order.expected_delivery_date)
    : '';

  let paymentCondition = '';
  if (order.payment_condition_type === 'cash') {
    paymentCondition = 'Al contado';
  } else if (order.payment_condition_type === 'days') {
    paymentCondition = `Cada ${order.payment_condition_days} d�as (m�x. 3 meses)`;
  } else if (order.payment_condition_type === 'date') {
    const paymentDateStr = formatPrintDate(order.payment_condition_date);
    paymentCondition = `Fecha espec�fica: ${paymentDateStr}`;
  }

  const paymentDateIso = resolveOrderPaymentDateIso(order);
  const paymentDateFormatted = paymentDateIso
    ? formatDateLocaleLima(paymentDateIso, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  const paymentDateNumeric =
    order.payment_condition_type !== 'cash' ? resolveOrderPaymentPrintDate(order) : '';

  const daysUntilPayment =
    paymentDateIso && order.order_date
      ? diffCalendarDays(order.order_date, paymentDateIso)
      : null;

  // Formatear valores
  const baseImponible = parseFloat(order.base_imponible || 0).toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  const igvAmount = parseFloat(order.igv_amount || 0).toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  const noGravados = parseFloat(order.no_gravados || 0).toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  const totalAmount = parseFloat(order.total_amount || 0).toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  // Convertir total a letras
  const numberToWords = (num) => {
    const entero = Math.floor(num);
    const decimal = Math.round((num - entero) * 100);
    return `${entero} CON ${decimal}/100 SOLES`;
  };
  const totalInWords = numberToWords(parseFloat(order.total_amount || 0));

  const statusText = order.status === 'issued' ? 'Emitida' : 'Cancelada';
  const statusColor = order.status === 'issued' ? '#10b981' : '#ef4444';

  return (
    <>
      <style>{`
        @page {
          size: A4;
          margin: 15mm 10mm;
        }
        
        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          body {
            margin: 0;
            padding: 0;
            background: white;
          }
          .no-print {
            display: none !important;
          }
          .print-container {
            padding: 0;
            margin: 0;
            max-width: 100%;
            box-shadow: none;
            width: 100%;
            height: 100%;
          }
        }
        
        @media screen {
          body {
            background: #f3f4f6;
          }
          .print-container {
            max-width: 210mm;
            margin: 20px auto;
            background: white;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          }
          .print-actions {
            position: fixed;
            top: 20px;
            right: 20px;
            z-index: 1000;
            display: flex;
            gap: 10px;
          }
        }
        
        * {
          box-sizing: border-box;
        }
        
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
          font-size: 11pt;
          color: #1f2937;
          line-height: 1.6;
          background: white;
        }
        
        .print-container {
          background: white;
          padding: 15mm 10mm;
          width: 210mm;
          min-height: 297mm;
        }
        
        @media print {
          .print-container {
            padding: 0;
            width: 100%;
            min-height: 100%;
          }
        }
        
        /* Header */
        .header {
          background: #0d9488;
          color: white;
          padding: 20px 25px;
          margin-bottom: 20px;
          border-radius: 0;
        }
        
        .header h1 {
          font-size: 22pt;
          font-weight: 700;
          margin: 0 0 8px 0;
          letter-spacing: -0.5px;
        }
        
        .header .code {
          font-family: 'Courier New', monospace;
          font-size: 14pt;
          font-weight: 600;
          letter-spacing: 1px;
        }
        
        /* Cards Grid */
        .cards-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
          margin-bottom: 20px;
        }
        
        .card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 0;
          padding: 16px;
        }
        
        .card-title {
          font-size: 10pt;
          font-weight: 600;
          color: #0d9488;
          margin: 0 0 14px 0;
          padding-bottom: 8px;
          border-bottom: 2px solid #e5e7eb;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        
        .card-title-icon {
          width: 14px;
          height: 14px;
        }
        
        @media print {
          .card-title-icon {
            display: none;
          }
        }
        
        .field-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin-bottom: 12px;
        }
        
        .field {
          margin-bottom: 12px;
        }
        
        .field-label {
          font-size: 7.5pt;
          font-weight: 600;
          color: #6b7280;
          margin-bottom: 4px;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }
        
        .field-value {
          font-size: 9.5pt;
          color: #1f2937;
          font-weight: 500;
          line-height: 1.4;
        }
        
        /* Payment Condition Card */
        .payment-card {
          padding: 16px;
        }
        
        .payment-info-simple {
          margin-top: 12px;
        }
        
        .payment-status {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid #e5e7eb;
        }
        
        .status-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 0;
          font-size: 8pt;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }
        
        .status-issued {
          background: #d1fae5;
          color: #065f46;
        }
        
        .status-cancelled {
          background: #fee2e2;
          color: #991b1b;
        }
        
        /* Items Card */
        .items-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 0;
          padding: 16px;
          margin-bottom: 20px;
        }
        
        .items-title {
          font-size: 10pt;
          font-weight: 600;
          color: #0d9488;
          margin: 0 0 14px 0;
          padding-bottom: 8px;
          border-bottom: 2px solid #e5e7eb;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        
        @media print {
          .items-title .card-title-icon {
            display: none;
          }
        }
        
        .items-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 16px;
          font-size: 8.5pt;
        }
        
        .items-table thead {
          background: #0d9488;
          color: white;
        }
        
        @media print {
          .items-table thead {
            background: #0d9488 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
        
        .items-table th {
          padding: 10px 8px;
          text-align: left;
          font-weight: 600;
          font-size: 8pt;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          border: none;
        }
        
        .items-table th:nth-child(1) {
          width: 35%;
        }
        .items-table th:nth-child(2) {
          width: 25%;
          text-align: left;
        }
        .items-table th:nth-child(3),
        .items-table th:nth-child(4),
        .items-table th:nth-child(5) {
          text-align: right;
        }
        .items-table th:nth-child(3) {
          width: 12%;
        }
        .items-table th:nth-child(4) {
          width: 19%;
        }
        .items-table th:nth-child(5) {
          width: 19%;
        }
        
        .items-table tbody tr {
          border-bottom: 1px solid #e5e7eb;
        }
        
        .items-table tbody tr:last-child {
          border-bottom: none;
        }
        
        .items-table td {
          padding: 10px 8px;
          font-size: 8.5pt;
          vertical-align: top;
        }
        
        .items-table td:nth-child(2) {
          text-align: left;
        }
        .items-table td:nth-child(3),
        .items-table td:nth-child(4),
        .items-table td:nth-child(5) {
          text-align: right;
        }
        
        .product-description-wrapper {
          display: flex;
          flex-direction: column;
        }
        
        .product-name {
          font-size: 8.5pt;
          color: #1f2937;
          font-weight: 500;
          line-height: 1.4;
          word-wrap: break-word;
          margin-bottom: 3px;
        }
        
        .product-code {
          font-size: 7pt;
          font-weight: 400;
          color: #6b7280;
          font-family: 'Courier New', monospace;
          letter-spacing: 0.2px;
          word-break: break-word;
          line-height: 1.2;
        }
        
        /* Totals */
        .totals-section {
          display: flex;
          justify-content: flex-end;
          margin-top: 16px;
        }
        
        .totals-box {
          width: 260px;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 0;
          padding: 14px;
        }
        
        .totals-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
          font-size: 8.5pt;
        }
        
        .totals-row:last-child {
          margin-bottom: 0;
        }
        
        .totals-label {
          color: #6b7280;
          font-weight: 500;
          text-transform: uppercase;
          font-size: 8pt;
          letter-spacing: 0.2px;
        }
        
        .totals-value {
          color: #1f2937;
          font-weight: 600;
          font-size: 9pt;
        }
        
        .totals-row.total {
          border-top: 2px solid #0d9488;
          padding-top: 10px;
          margin-top: 10px;
          font-size: 11pt;
        }
        
        .totals-row.total .totals-label {
          color: #0d9488;
          font-size: 9.5pt;
          font-weight: 700;
        }
        
        .totals-row.total .totals-value {
          color: #0d9488;
          font-size: 13pt;
          font-weight: 800;
        }
        
        .total-words {
          margin-top: 14px;
          font-size: 8.5pt;
          color: #4b5563;
          text-align: right;
          font-style: italic;
          padding-right: 4px;
        }
        
        .total-words strong {
          font-weight: 700;
          color: #1f2937;
          font-style: normal;
        }
        
        /* Observations */
        .observations-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 0;
          padding: 16px;
          margin-bottom: 20px;
        }
        
        .observations-title {
          font-size: 10pt;
          font-weight: 600;
          color: #0d9488;
          margin: 0 0 12px 0;
          padding-bottom: 8px;
          border-bottom: 2px solid #e5e7eb;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        
        @media print {
          .observations-title .card-title-icon {
            display: none;
          }
        }
        
        .observations-content {
          font-size: 9pt;
          color: #374151;
          line-height: 1.6;
          white-space: pre-wrap;
        }
        
        /* Footer */
        .footer {
          margin-top: 20px;
          padding-top: 12px;
          border-top: 1px solid #e5e7eb;
          text-align: center;
          font-size: 7pt;
          color: #9ca3af;
          font-style: italic;
        }
      `}</style>

      <div className="print-actions no-print">
        <button
          onClick={() => window.print()}
          className="px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium shadow-md transition-colors"
        >
          Imprimir
        </button>
        <button
          onClick={() => window.close()}
          className="px-5 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium shadow-md transition-colors"
        >
          Cerrar
        </button>
      </div>

      <div className="print-container">
        {/* Header */}
        <div className="header">
          <h1>ORDEN DE SERVICIO</h1>
          <div className="code">{order.code}</div>
        </div>

        {/* Cards Grid */}
        <div className="cards-grid">
          {/* Informaci�n B�sica */}
          <div className="card">
            <div className="card-title">
              <svg className="card-title-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Informaci�n B�sica
            </div>
            <div className="field-row">
              <div className="field">
                <div className="field-label">Proveedor</div>
                <div className="field-value">{order.supplier?.name || 'N/A'}</div>
              </div>
              {order.supplier?.document_type && order.supplier?.document_number && (
                <div className="field">
                  <div className="field-label">{order.supplier.document_type.toUpperCase()}</div>
                  <div className="field-value">{order.supplier.document_number}</div>
                </div>
              )}
            </div>
            {order.supplier?.address && (
              <div className="field">
                <div className="field-label">Direcci�n</div>
                <div className="field-value">{order.supplier.address}</div>
              </div>
            )}
            <div className="field-row">
              <div className="field">
                <div className="field-label">Fecha de Orden</div>
                <div className="field-value">{orderDate}</div>
              </div>
              {deliveryDate && (
                <div className="field">
                  <div className="field-label">Fecha Esperada de Entrega</div>
                  <div className="field-value">{deliveryDate}</div>
                </div>
              )}
            </div>
          </div>

          {/* Condici�n de Pago */}
          <div className="card payment-card">
            <div className="card-title">
              <svg className="card-title-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Condici�n de Pago
            </div>
            <div className="field">
              <div className="field-label">Tipo de Condici�n</div>
              <div className="field-value">{paymentCondition}</div>
            </div>
            {order.payment_condition_type !== 'cash' && paymentDateNumeric && (
              <div className="payment-info-simple">
                <div className="field">
                  <div className="field-label">Fecha de Pago</div>
                  <div className="field-value">{paymentDateNumeric}</div>
                </div>
                {daysUntilPayment !== null && (
                  <div className="field">
                    <div className="field-label">D�as hasta el pago</div>
                    <div className="field-value">
                      {daysUntilPayment} {daysUntilPayment === 1 ? 'd�a' : 'd�as'}
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="payment-status">
              <div className="field">
                <div className="field-label">Estado</div>
                <div>
                  <span className={`status-badge status-${order.status}`}>
                    {statusText}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Items Card */}
        <div className="items-card">
          <div className="items-title">
            <svg className="card-title-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            Items de la Orden
          </div>
          <table className="items-table">
            <thead>
              <tr>
                <th>SERVICIO</th>
                <th>DESCRIPCI�N</th>
                <th>CANT.</th>
                <th>P.UNIT</th>
                <th>TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {(order.items || []).map((item, index) => {
                const quantity = parseFloat(item.quantity || 0).toLocaleString('es-PE', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                });
                const unitPrice = parseFloat(item.unit_price || 0).toLocaleString('es-PE', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                });
                const total = parseFloat(item.total || 0).toLocaleString('es-PE', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                });
                return (
                  <tr key={index}>
                    <td>
                      <div className="product-description-wrapper">
                        <span className="product-name">{item.service?.name || 'N/A'}</span>
                        <span className="product-code">{item.service?.sku || 'N/A'}</span>
                      </div>
                    </td>
                    <td>{item.description || '-'}</td>
                    <td>{quantity}</td>
                    <td>S/ {unitPrice}</td>
                    <td><strong>S/ {total}</strong></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="totals-section">
            <div className="totals-box">
              <div className="totals-row">
                <span className="totals-label">Base Imponible:</span>
                <span className="totals-value">S/ {baseImponible}</span>
              </div>
              <div className="totals-row">
                <span className="totals-label">IGV (18%):</span>
                <span className="totals-value">S/ {igvAmount}</span>
              </div>
              {parseFloat(order.no_gravados || 0) > 0 && (
                <div className="totals-row">
                  <span className="totals-label">No Gravados:</span>
                  <span className="totals-value">S/ {noGravados}</span>
                </div>
              )}
              <div className="totals-row total">
                <span className="totals-label">Total:</span>
                <span className="totals-value">S/ {totalAmount}</span>
              </div>
            </div>
          </div>
          <div className="total-words">
            <strong>SON:</strong> {totalInWords}
          </div>
        </div>

        {/* Observations */}
        {order.notes && (
          <div className="observations-card">
            <div className="observations-title">
              <svg className="card-title-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Observaciones
            </div>
            <div className="observations-content">{order.notes}</div>
          </div>
        )}

        {/* Footer */}
        <div className="footer">
          Documento generado el {new Date().toLocaleString('es-PE', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit' 
          })}
        </div>
      </div>
    </>
  );
};

export default PrintServiceOrder;
